import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button, Chip, TextInput } from '@c2fo/liquidity';
import { AngleDownIcon, AngleRightIcon } from '@c2fo/liquidity/icons';
import { LoadingOverlay } from '../LoadingSpinner';
import { BackLink } from '../BackLink';
import { Card } from '../Card';
import { CardFooter } from '../CardFooter';
import { FormField } from '../FormField';
import { NotFoundCard } from '../NotFoundCard';
import { PageContainer } from '../PageContainer';
import { useSchedulesState } from '../../contexts';
import { updateScheduledDelivery } from '../../api/ScheduledDeliveries';
import { ScheduleType, type PreDeliveryAction, type PostDeliveryAction, type RetryConfig } from '../../api/shared';
import { useErrorState } from '../ErrorBanner';
import { useApiContext } from '../../contexts/FileDeliveryContext';
import { LocationSelector } from '../LocationSelector';
import { ScheduleEditor } from '../ScheduleEditor';
import { DeliveryActionsEditor } from '../DeliveryActionsEditor';
import { RetryConfigEditor } from '../RetryConfigEditor';
import { getScheduleStateChipType, getScheduleStateLabel } from '../../utils';

export const ScheduleForm: React.FC = () => {
  const { scheduleUid } = useParams<{ scheduleUid: string }>();
  const navigate = useNavigate();
  const { addError } = useErrorState();
  const apiContext = useApiContext();
  const {
    currentSchedule: schedule,
    isLoadingSchedule: isLoading,
    fetchSchedule,
    clearCurrentSchedule,
  } = useSchedulesState();

  const [displayName, setDisplayName] = useState('');
  const [sourceLocation, setSourceLocation] = useState('');
  const [sourcePath, setSourcePath] = useState('');
  const [targetLocation, setTargetLocation] = useState('');
  const [targetPath, setTargetPath] = useState('');
  const [scheduleType, setScheduleType] = useState<ScheduleType>(ScheduleType.SCHEDULE_TYPE_CRON);
  const [scheduleExpression, setScheduleExpression] = useState('');
  const [scheduleTimezone, setScheduleTimezone] = useState('UTC');
  const [ignoreEmptySource, setIgnoreEmptySource] = useState(false);
  const [preDeliveryAction, setPreDeliveryAction] = useState<PreDeliveryAction | undefined>(undefined);
  const [postDeliveryAction, setPostDeliveryAction] = useState<PostDeliveryAction | undefined>(undefined);
  const [retryConfig, setRetryConfig] = useState<RetryConfig | undefined>(undefined);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (scheduleUid) {
      // Construct full path: scheduledDeliveries/{scheduleUid}
      fetchSchedule(`scheduledDeliveries/${scheduleUid}`);
    }

    return () => {
      clearCurrentSchedule();
    };
  }, [scheduleUid, fetchSchedule, clearCurrentSchedule]);

  useEffect(() => {
    if (schedule) {
      setDisplayName(schedule.displayName ?? '');
      setSourceLocation(schedule.sourceLocation?.uid ?? '');
      setSourcePath(schedule.sourcePath);
      setTargetLocation(schedule.targetLocation?.uid ?? '');
      setTargetPath(schedule.targetPath);
      setScheduleType(schedule.schedule.type);
      setScheduleExpression(schedule.schedule.expression);
      setScheduleTimezone(schedule.schedule.timezone ?? 'UTC');
      setIgnoreEmptySource(schedule.ignoreEmptySource ?? false);
      setPreDeliveryAction(schedule.preDeliveryAction);
      setPostDeliveryAction(schedule.postDeliveryAction);
      setRetryConfig(schedule.retryConfig);
      setShowAdvanced(!!(schedule.preDeliveryAction ?? schedule.postDeliveryAction ?? schedule.retryConfig));
    }
  }, [schedule]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schedule) return;

    setIsSaving(true);
    try {
      await updateScheduledDelivery(apiContext, {
        scheduledDelivery: {
          name: schedule.name,
          displayName,
          sourceLocation: `locations/${sourceLocation}`,
          sourcePath,
          targetLocation: `locations/${targetLocation}`,
          targetPath,
          schedule: {
            type: scheduleType,
            expression: scheduleExpression,
            timezone: scheduleTimezone,
          },
          ignoreEmptySource,
          preDeliveryAction,
          postDeliveryAction,
          retryConfig,
        },
        updateMask: [
          'display_name',
          'source_location',
          'source_path',
          'target_location',
          'target_path',
          'schedule',
          'ignore_empty_source',
          'pre_delivery_action',
          'post_delivery_action',
          'retry_config',
        ],
      });
      navigate(`/file-delivery/scheduledDeliveries/${scheduleUid}`);
    } catch {
      addError({ id: 'update-schedule-error', message: 'Failed to update scheduled delivery', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingOverlay message="Loading schedule..." />;
  }

  if (!schedule) {
    return (
      <PageContainer>
        <BackLink to="/file-delivery/scheduledDeliveries" label="Back to Schedules" />
        <NotFoundCard message="Schedule not found or could not be loaded." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink to={`/file-delivery/scheduledDeliveries/${scheduleUid}`} label="Back to Schedule Details" />

      <form onSubmit={handleSubmit}>
        <Card>
          <div className="px-6 pt-4 pb-4 border-b border-gray-200">
            <Chip
              label={getScheduleStateLabel(schedule.state)}
              type={getScheduleStateChipType(schedule.state)}
              variant="filled"
              size="xs"
            />
            <h2 className="text-lg font-medium mt-3 text-gray-900">
              Editing {schedule.displayName || 'Scheduled Delivery'}
            </h2>
          </div>

          <div className="px-6 py-6 space-y-6">
            <FormField label="Display Name">
              <TextInput
                value={displayName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
                placeholder="Enter display name"
                className="w-full"
                size="sm"
              />
            </FormField>

            <div className="grid grid-cols-2 gap-6">
              <FormField label="Source Location" required>
                <LocationSelector
                  value={sourceLocation}
                  onChange={setSourceLocation}
                  onError={(message) => addError({ id: 'location-error', message, type: 'error' })}
                  placeholder="Select source location"
                />
              </FormField>
              <FormField label="Source Path" required>
                <TextInput
                  value={sourcePath}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSourcePath(e.target.value)}
                  placeholder="e.g., /path/to/files/*.csv"
                  className="w-full font-mono"
                  size="sm"
                />
                <p className="mt-1 text-xs text-gray-500">Supports glob patterns</p>
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <FormField label="Target Location" required>
                <LocationSelector
                  value={targetLocation}
                  onChange={setTargetLocation}
                  onError={(message) => addError({ id: 'location-error', message, type: 'error' })}
                  placeholder="Select target location"
                />
              </FormField>
              <FormField label="Target Path" required>
                <TextInput
                  value={targetPath}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTargetPath(e.target.value)}
                  placeholder="e.g., /destination/folder/"
                  className="w-full font-mono"
                  size="sm"
                />
              </FormField>
            </div>

            <ScheduleEditor
              scheduleType={scheduleType}
              expression={scheduleExpression}
              timezone={scheduleTimezone}
              onTypeChange={setScheduleType}
              onExpressionChange={setScheduleExpression}
              onTimezoneChange={setScheduleTimezone}
            />

            <div className="flex items-center">
              <input
                type="checkbox"
                id="ignoreEmptySource"
                checked={ignoreEmptySource}
                onChange={(e) => setIgnoreEmptySource(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="ignoreEmptySource" className="ml-2 text-sm text-gray-700">
                Ignore empty source (don&apos;t fail when source is empty)
              </label>
            </div>

            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {showAdvanced ? <AngleDownIcon className="h-4 w-4" /> : <AngleRightIcon className="h-4 w-4" />}
              Advanced Settings
            </button>

            {showAdvanced && (
              <div className="space-y-6 pl-5 border-l-2 border-gray-200">
                <DeliveryActionsEditor
                  preDeliveryAction={preDeliveryAction}
                  postDeliveryAction={postDeliveryAction}
                  onPreDeliveryActionChange={setPreDeliveryAction}
                  onPostDeliveryActionChange={setPostDeliveryAction}
                />

                <RetryConfigEditor retryConfig={retryConfig} onChange={setRetryConfig} />
              </div>
            )}
          </div>

          <CardFooter>
            <Link to={`/file-delivery/scheduledDeliveries/${scheduleUid}`}>
              <Button variant="tertiary" className="rounded-md" type="button">
                Cancel
              </Button>
            </Link>
            <Button
              variant="primary"
              className="rounded-md text-white"
              type="submit"
              disabled={
                isSaving || !sourceLocation || !sourcePath || !targetLocation || !targetPath || !scheduleExpression
              }
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </PageContainer>
  );
};
